/* Lucide icon shim ─────────────────────────────────────────────────────────
 * The admin's icon markup is Bootstrap Icons (<i class="bi bi-name">), a
 * fixed-weight webfont with no adjustable stroke. To get lighter, thinner
 * icons we swap each glyph in-place for the matching Lucide SVG rendered at
 * stroke-width 1 (half Lucide's default of 2) — without touching any view or
 * generated markup. The original <i> element (and its classes) is kept so
 * existing CSS selectors, spin animations and `color` still apply; we just
 * fill it with an SVG and suppress the ::before glyph.
 *
 * Anything not in MAP — brand logos (spotify/apple/…) and rare glyphs — is
 * left on the Bootstrap font. Names that don't exist in the loaded Lucide
 * build also fall back to the font (existence-checked), so a bad guess is
 * never a broken icon.
 */
(function () {
  'use strict';

  // Bootstrap Icons name → Lucide name (kebab). Brands + a few niche glyphs
  // are intentionally omitted so they stay on the recognisable Bootstrap font.
  var MAP = {
    'x-lg': 'x', 'x': 'x', 'x-circle': 'x-circle', 'x-circle-fill': 'x-circle',
    'plus-lg': 'plus', 'plus': 'plus', 'plus-circle': 'plus-circle', 'plus-square': 'plus-square',
    'dash': 'minus', 'dash-circle': 'minus-circle',
    'check': 'check', 'check2': 'check', 'check-lg': 'check',
    'check-circle': 'check-circle', 'check-circle-fill': 'check-circle',
    'pencil': 'pencil', 'pencil-square': 'pencil',
    'trash': 'trash-2', 'trash3': 'trash-2',
    'person': 'user', 'person-fill': 'user', 'person-circle': 'user', 'person-workspace': 'user',
    'person-plus': 'user-plus', 'person-add': 'user-plus', 'person-dash': 'user-minus',
    'person-x': 'user-x', 'person-lock': 'user', 'person-badge': 'user',
    'people': 'users', 'people-fill': 'users',
    'disc': 'disc', 'vinyl': 'disc',
    'grip-vertical': 'grip-vertical', 'three-dots-vertical': 'more-vertical', 'three-dots': 'more-horizontal',
    'chevron-down': 'chevron-down', 'chevron-up': 'chevron-up',
    'chevron-left': 'chevron-left', 'chevron-right': 'chevron-right', 'sort-down': 'chevron-down',
    'play-fill': 'play', 'play-circle': 'play-circle',
    'pause-fill': 'pause', 'stop-circle': 'circle-stop',
    'arrow-repeat': 'repeat', 'arrow-clockwise': 'rotate-cw', 'arrow-counterclockwise': 'rotate-ccw',
    'arrow-left': 'arrow-left', 'arrow-right': 'arrow-right', 'arrow-right-short': 'arrow-right',
    'arrow-up-right': 'arrow-up-right', 'arrow-return-left': 'corner-down-left',
    'arrow-return-right': 'corner-down-right',
    'download': 'download', 'upload': 'upload',
    'cloud-arrow-up': 'cloud-upload', 'cloud-upload': 'cloud-upload',
    'cloud-download': 'cloud-download', 'cloud-slash': 'cloud-off',
    'link-45deg': 'link', 'link': 'link', 'box-arrow-up-right': 'external-link',
    'box-arrow-right': 'log-out', 'box-arrow-left': 'log-in',
    'eye': 'eye', 'eye-slash': 'eye-off',
    'image': 'image', 'image-fill': 'image', 'images': 'images', 'film': 'film',
    'exclamation-circle': 'alert-circle', 'exclamation-triangle': 'alert-triangle',
    'exclamation-triangle-fill': 'alert-triangle', 'info-circle': 'info',
    'folder2-open': 'folder-open', 'collection': 'library', 'collection-play': 'play-circle',
    'music-note': 'music', 'music-note-beamed': 'music', 'music-note-list': 'list-music',
    'building': 'building-2', 'search': 'search', 'clock-history': 'history', 'clock': 'clock',
    'qr-code': 'qr-code', 'upc-scan': 'scan', 'camera': 'camera', 'camera-fill': 'camera',
    'camera-video': 'video', 'camera-reels': 'video', 'broadcast': 'radio',
    'share': 'share-2', 'phone': 'smartphone', 'phone-fill': 'smartphone', 'phone-flip': 'smartphone',
    'lightbulb': 'lightbulb', 'envelope': 'mail', 'envelope-fill': 'mail',
    'envelope-exclamation': 'mail', 'copy': 'copy', 'calendar': 'calendar', 'calendar3': 'calendar',
    'calendar-check': 'calendar-check', 'calendar-x': 'calendar-x', 'bell': 'bell', 'bag': 'shopping-bag',
    'arrows-fullscreen': 'maximize', 'arrows-angle-expand': 'maximize',
    'arrows-angle-contract': 'minimize', 'wrench': 'wrench',
    'patch-check-fill': 'badge-check', 'patch-check': 'badge-check',
    'keyboard': 'keyboard', 'hourglass-split': 'hourglass', 'headphones': 'headphones',
    'grid': 'layout-grid', 'grid-3x2-gap': 'layout-grid', 'geo-alt': 'map-pin', 'gear': 'settings',
    'gear-fill': 'settings', 'emoji-smile': 'smile',
    'bar-chart': 'bar-chart-2', 'bar-chart-line': 'bar-chart-2', 'activity': 'activity',
    'volume-up': 'volume-2', 'volume-mute': 'volume-x',
    'type-underline': 'underline', 'type-strikethrough': 'strikethrough',
    'type-italic': 'italic', 'type-bold': 'bold',
    'square': 'square', 'scissors': 'scissors',
    'pin-fill': 'pin', 'pin-angle-fill': 'pin', 'pin-angle': 'pin', 'pin': 'pin',
    'house-door-fill': 'home', 'house-door': 'home', 'house-add': 'home',
    'heart-fill': 'heart', 'heart': 'heart', 'display': 'monitor', 'laptop': 'laptop',
    'chat-left-text': 'message-square', 'bookmark': 'bookmark', 'bookmark-plus': 'bookmark-plus',
    'window': 'app-window', 'trophy': 'trophy', 'stars': 'sparkles', 'magic': 'sparkles',
    'sliders': 'sliders-horizontal', 'shield-lock': 'shield', 'rocket': 'rocket',
    'moon-stars': 'moon', 'list-ul': 'list', 'list': 'list', 'key': 'key',
    'journal-code': 'file-code', 'unlock': 'unlock', 'hand-index-fill': 'hand', 'globe': 'globe', 'fire': 'flame', 'graph-up-arrow': 'trending-up',
    'file-earmark-pdf': 'file-text', 'file-earmark-text': 'file-text', 'file-earmark': 'file', 'diagram-3': 'git-branch',
    'database': 'database', 'credit-card': 'credit-card', 'clipboard': 'clipboard',
    'clipboard-check': 'clipboard-check', 'circle-half': 'contrast', 'circle-fill': 'circle',
    'circle': 'circle', 'bug': 'bug',
    'lightning-charge': 'zap', 'lightning-charge-fill': 'zap', 'lightning': 'zap', 'capsule': 'pill'
  };

  function pascal(kebab) {
    return kebab.split('-').map(function (s) {
      return s.charAt(0).toUpperCase() + s.slice(1);
    }).join('');
  }

  // Build an <svg> string from a Lucide icon node. Lucide ships each icon as a full
  // tuple ["svg", svgAttrs, children[]]; we only want the children (paths/circles/…)
  // and render them under our own <svg> at stroke-width 1.
  function svgFrom(node) {
    var children = (Array.isArray(node) && node.length >= 3 && Array.isArray(node[2]))
      ? node[2] : node;
    var inner = children.map(function (child) {
      var tag = child[0], attrs = child[1] || {};
      var a = Object.keys(attrs).filter(function (k) { return k !== 'key'; })
        .map(function (k) { return k + '="' + attrs[k] + '"'; }).join(' ');
      return '<' + tag + (a ? ' ' + a : '') + '/>';
    }).join('');
    return '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" ' +
      'stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" ' +
      'class="li-svg" aria-hidden="true">' + inner + '</svg>';
  }

  var cache = {};
  function iconSvg(luName) {
    if (luName in cache) return cache[luName];
    var lucide = window.lucide;
    var node = lucide && lucide.icons && lucide.icons[pascal(luName)];
    cache[luName] = node ? svgFrom(node) : null;
    return cache[luName];
  }

  function convert(root) {
    if (!window.lucide || !window.lucide.icons) return;
    var els = (root || document).querySelectorAll('i[class*="bi-"]:not(.li-swapped)');
    for (var i = 0; i < els.length; i++) {
      var el = els[i];
      var m = /\bbi-([a-z0-9]+(?:-[a-z0-9]+)*)/.exec(el.className);
      if (!m) continue;
      var lu = MAP[m[1]];
      if (!lu) continue;                 // unmapped → leave Bootstrap glyph
      var svg = iconSvg(lu);
      if (!svg) continue;                // name missing in this Lucide build → font fallback
      el.innerHTML = svg;
      el.classList.add('li-swapped');
    }
  }

  var scheduled = false;
  function scheduleConvert() {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(function () { scheduled = false; convert(document); });
  }

  function start() {
    convert(document);
    // Icons are also injected dynamically (visual editor, grids, modals) — keep up.
    if (window.MutationObserver) {
      var obs = new MutationObserver(function (muts) {
        for (var i = 0; i < muts.length; i++) {
          if (muts[i].addedNodes && muts[i].addedNodes.length) { scheduleConvert(); return; }
        }
      });
      obs.observe(document.body, { childList: true, subtree: true });
    }
  }

  // Wait for the Lucide UMD bundle (deferred) to be present.
  function whenReady() {
    if (window.lucide && window.lucide.icons) { start(); return; }
    var tries = 0;
    var t = setInterval(function () {
      if ((window.lucide && window.lucide.icons) || ++tries > 60) {
        clearInterval(t);
        if (window.lucide && window.lucide.icons) start();
      }
    }, 50);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', whenReady);
  } else {
    whenReady();
  }
})();
